import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Stack,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";

import { auth, db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// 🔴 GOOGLE APPS SCRIPT URL
const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzq5il3JKKJDYwn_UcfCh8mfeZ2fe_TehyFlKnl2AiJg9XnKus8chg5LFK-LUkZ8BxT/exec";

const ServiceRequest = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const service = state?.service;
  const user = auth.currentUser;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!service) {
    return (
      <Container sx={{ pt: 12 }}>
        <Typography color="error">
          No service selected. Please go back.
        </Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/services")}>
          Back to Services
        </Button>
      </Container>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d{0,10}$/.test(value)) return;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Enter a valid email address";
    if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Phone number must be exactly 10 digits";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmailViaGoogleSheet = async (payload) => {
    const formBody = new URLSearchParams(payload).toString();
    await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formBody,
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "service_requests"), {
        userId: user?.uid || null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        serviceId: service.id,
        serviceTitle: service.title,
        category: service.category,
        price: service.price,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      await sendEmailViaGoogleSheet({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        service: service.title,
        category: service.category,
        price: service.price,
      });

      setSuccess(true);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      console.error(err);
      alert("Failed to order service. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
        maxWidth="md"
        sx={{
          pt: { xs: 10, md: 14 },
          pb: 10,
          minHeight: "100vh",
          color: "#2B211C",
          "& .MuiCard-root": {
            borderRadius: "22px",
            border: "1px solid #DCD4CC",
            backgroundColor: "#FCFAF600",
            boxShadow: "0 18px 55px rgba(43,33,28,0.08)",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "13px",
            backgroundColor: "#FFFDF8",
          },
        }}
      >
      {/* 🔹 PAGE TITLE */}
      <Typography variant="h3" mb={4} fontWeight={800} fontFamily="Georgia, Times New Roman, serif">
        Order Service
      </Typography>

      {/* SERVICE SUMMARY */}
      <Card sx={{ mb: 4, p: { xs: 2.5, md: 3.5 } }}>
        <CardContent>
          <Typography fontWeight={700}>{service.title}</Typography>
          <Typography color="text.secondary">{service.category}</Typography>
          <Typography mt={1} fontWeight={600}>
            ₹{service.price}
          </Typography>
        </CardContent>
      </Card>

      {/* FORM */}
      <Card sx={{ overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography variant="h6" mb={2}>
            Your Details
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            <TextField
              label="Full Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
            />

            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
            />

            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone || "Enter 10-digit mobile number"}
              required
              fullWidth
            />

            <TextField
              label="Message (Optional)"
              name="message"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />

            {/* 🔹 BUTTON TEXT */}
            <Button
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: "#2B211C",
                mt: 1,
                borderRadius: "12px",
                py: 1.35,
                textTransform: "none",
                "&:hover": { bgcolor: "#333" },
              }}
              onClick={handleSubmit}
            >
              {loading ? "Processing..." : "Order Now"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* 🔹 SUCCESS MESSAGE */}
      <Snackbar open={success} autoHideDuration={3000}>
        <Alert severity="success">
          Service ordered successfully 📩
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ServiceRequest;
